import { useMemo } from "react";
import { animationUrlFor } from "@heraion/shared";
import {
  useAthleteTargets,
  usePrograms,
  uuidv4,
  type LoggerHostDeps,
} from "@heraion/ui";

import { apiFetch, ApiError } from "../api";
import { durableWrite } from "../durableWrite";
import { animationAccess } from "../animationAccess";
import { confirmViaWindow, notifyViaWindow } from "../confirm";
import { useShellProfile } from "../ThemeProfileBridge";
import {
  invalidateSelfSessionResults,
  selfSessionResultsDeps,
} from "../progress/progressDeps";
import { exerciseCatalog } from "./exercisesCache";
import { ExercisePickerMount } from "../ExercisePickerMount";
import { ExerciseTimerSheet } from "./ExerciseTimerSheet";
import { EffortScalePicker } from "./EffortScalePicker";
import { useLoggerProfile } from "./useLoggerProfile";
import { PostSessionSummaryCard } from "./PostSessionSummaryCard";
import { PainDot, usePainRiskLabel } from "../PainDotWeb";
import { playTimerCue } from "./timerCueAudio";
import { useMaxesDataWeb } from "../maxes/useMaxesDataWeb";

// Assembles web-rnw's LoggerHostDeps (RW-18) — the web twin of
// apps/mobile/src/screens/useLoggerHostDeps.ts. Every field mirrors the
// mobile hook's SHAPE (see hostDeps.ts for the full contract); the
// difference is always the platform mechanism behind it, never the data:
//   - durableWrite/shouldQueueError/queueWorkout: web has no offline queue
//     (seams.ts) — every write is online-only, `sent` or `rejected`, never
//     `queued`; queueWorkout always rejects (nothing to enqueue INTO).
//   - catalog/profile/programs/targets/maxes: web's own network-only
//     equivalents of the AsyncStorage-backed mobile modules, several
//     already shared via @heraion/ui (useAthleteTargets, useMaxesData,
//     usePrograms) rather than duplicated per-host.
//   - ExercisePickerMount/ExerciseTimerSheet/EffortScalePicker/
//     PostSessionSummaryCard: web's own thin mounts of the shared
//     component trees (see each file's own header comment).
//   - timerNotification: omitted (capability-gating by omission — a
//     native-only slot, see hostDeps.ts's own doc on it).
//   - playTimerCue: WIRED (HI-3 slice 2b, 2026-08-30) — the foreground timer
//     cues are NOT a native-only slot, and this is the first sound the web
//     logger makes. timerCueAudio.ts synthesises them; mobile's twin routes
//     the same events through a notification channel.
//   - PainDot/usePainRiskLabel: wired 2026-08-17 via PainDotWeb.tsx —
//     painMap has been a web-rnw surface since the pain-map-picker lift
//     (2026-08-16), so this is no longer native-only.
// #343: null until the profile is read, so the Logger does not open on a kg guess.
/** `open`: the Logger is on screen, so its unit holds (#343, useLoggerProfile). */
export function useLoggerHostDeps(open = false): LoggerHostDeps | null {
  const { savePatch, refresh: refreshProfileDoc } = useShellProfile();
  const { value: athleteTargets } = useAthleteTargets({ apiFetch });
  const programsState = usePrograms({ apiFetch, durableWrite, withProgress: false });
  const { maxMeta, loaded: maxesLoaded } = useMaxesDataWeb();

  // PE-39/K5: the profile memo lives in useLoggerProfile. PlanScreen mounts
  // the prescribe editor and needs the same object without the rest of this bag.
  const profile = useLoggerProfile(open);

  return useMemo<LoggerHostDeps | null>(
    () => profile === null ? null : ({
      apiFetch,
      durableWrite,
      notify: notifyViaWindow,
      confirm: confirmViaWindow,
      playTimerCue,
      profile,
      // Past the null above, the doc is read.
      profileLoaded: true,
      refreshProfile: refreshProfileDoc,
      patchProfile: async (patch) => {
        await savePatch(patch);
      },
      athleteTargets,
      sessionResultsDeps: selfSessionResultsDeps,
      followedPrograms: programsState.followedPrograms,
      maxMeta,
      maxesLoaded,
      catalog: exerciseCatalog,
      // Web has no offline queue (seams.ts) — every failure surfaces.
      offerSaveAsNewWhenGone: true,
      shouldQueueError: () => false,
      queueWorkout: async () => {
        throw new ApiError(0, "no_offline_queue", null);
      },
      saveNoteEvent: async (note) => {
        try {
          await apiFetch("/api/me/workout-notes", {
            method: "POST",
            body: { ...note, id: note.id ?? uuidv4() },
          });
        } catch (err: unknown) {
          // Best-effort: the workout is already saved. Never throws.
          console.warn("[notes] save failed", err);
        }
      },
      updateNoteEvent: async (id, text) => {
        try {
          await apiFetch(`/api/me/workout-notes/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: { text },
          });
        } catch (err: unknown) {
          // Best-effort, like saveNoteEvent. Never throws.
          console.warn("[notes] update failed", err);
        }
      },
      deleteNoteEvent: async (id) => {
        try {
          await apiFetch(`/api/me/workout-notes/${encodeURIComponent(id)}`, {
            method: "DELETE",
          });
        } catch (err: unknown) {
          // Best-effort, like saveNoteEvent. Never throws.
          console.warn("[notes] delete failed", err);
        }
      },
      saveExerciseGroup: async (name, exerciseSlugs) => {
        await apiFetch("/api/me/exercise-groups", {
          method: "POST",
          body: { id: uuidv4(), name, exerciseSlugs },
        });
      },
      invalidateSessionResults: invalidateSelfSessionResults,
      ExercisePickerMount,
      ExerciseTimerSheet,
      EffortScalePicker,
      PostSessionSummaryCard,
      // AN-2/AN-3 — il gemello della riga in apps/mobile. Stesso resolver
      // condiviso, base diversa perche' la porta il bundle invece dell'APK.
      // ⛔ Sul web non serve nessuna cache nostra: e' la cache HTTP del
      // browser, e la GIF che l'atleta ha gia' aperto non si riscarica.
      animationSource: (slug, widthPx, variant) =>
        animationUrlFor(import.meta.env.VITE_ANIMATIONS_URL, slug, widthPx, variant),
      // AN-7: same seam as the mobile mount, so a cookie rotation does not
      // leave the athlete stuck for the rest of the session.
      onAnimationLoadError: animationAccess.renew,
      PainDot,
      usePainRiskLabel,
    }),
    [
      profile,
      refreshProfileDoc,
      savePatch,
      athleteTargets,
      programsState.followedPrograms,
      maxMeta,
      maxesLoaded,
    ],
  );
}
